// Off-canvas "Logos-Kontext" panel (ADR-0029, Phase 2). Renders the list of
// currently attached context blocks from the shared state and lets the user
// remove them. Excerpt paste and bridge search are added in later steps.
import { getBlocks, addBlock, removeBlockAt, onBlocksChanged } from './context_state.js';
import { bridgeFetch, probeHealth } from './bridge_client.js';
import { getPlatform } from './platform.js';
import { compareVersions } from './version.js';

const TYPE_ICONS = {
    upload: 'fas fa-paperclip',
    bible_passage: 'fas fa-book-open',
    logos_note: 'fas fa-note-sticky',
    logos_excerpt: 'fas fa-quote-right'
};

// Mirrors MAX_EXCERPT_CHARS in app/services/context_service.py.
const MAX_EXCERPT_CHARS = 15000;

// --- Local bridge state (ADR-0029 Phase 3 / ADR-0030) -----------------------
// The shared HTTP client lives in bridge_client.js. Health probes never run
// on page load — only while the panel is open (polling until detected).
const BRIDGE_POLL_INTERVAL_MS = 2500;

let bridgeDetected = false;

// chat.js reads this to decide whether "In Logos öffnen" footer buttons make
// sense; false until the health probe has succeeded.
export function isBridgeDetected() {
    return bridgeDetected;
}

export async function openInLogos(url) {
    const response = await bridgeFetch('/open', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
    });
    if (!response.ok) throw new Error(`Bridge /open failed: HTTP ${response.status}`);
}

async function bridgeSearch(path, query) {
    const response = await bridgeFetch(`${path}?q=${encodeURIComponent(query)}`);
    if (!response.ok) throw new Error(`Bridge search failed: HTTP ${response.status}`);
    const body = await response.json();
    return body.results || [];
}

// Heuristic parser for the citation line Logos appends when "copy with
// citation" is enabled. Logos puts it as the last line, typically:
//   "Author, Work (Place: Publisher, Year), 123."
// If the last non-empty line looks like such a citation, its author/work part
// becomes the title and the trailing page(s) the locator; both are stripped
// from the content. On no match, everything stays in content.
export function parseLogosCitation(text) {
    const noMatch = { content: text, title: '', locator: '' };
    const lines = text.split('\n');
    while (lines.length && lines[lines.length - 1].trim() === '') lines.pop();
    if (lines.length === 0) return noMatch;

    const last = lines[lines.length - 1].trim();
    const pageMatch = last.match(/,\s*(?:S\.?\s*|pp?\.?\s*)?(\d+(?:\s*[–-]\s*\d+)?)\.?$/);
    const hasYearOrPageMarker =
        /\([^)]*\b\d{4}\b[^)]*\)/.test(last) || /\b(?:S\.|pp?\.)\s*\d/.test(last);
    if (!pageMatch || !last.includes(',') || !hasYearOrPageMarker) return noMatch;

    const title = last.slice(0, pageMatch.index).replace(/[,;\s]+$/, '');
    if (!title) return noMatch;

    lines.pop();
    while (lines.length && lines[lines.length - 1].trim() === '') lines.pop();
    const content = lines.join('\n');
    if (content.trim() === '') return noMatch;

    return {
        content,
        title,
        locator: pageMatch[1].replace(/\s*[–-]\s*/, '–')
    };
}

export function initializeContextPanel() {
    const panel = document.getElementById('contextPanel');
    if (!panel) return;

    const list = document.getElementById('contextBlockList');
    const emptyHint = document.getElementById('contextBlockEmpty');

    // Titles and locators are user-controlled; build rows via DOM APIs and
    // textContent so they are always escaped.
    function buildRow(block, index) {
        const row = document.createElement('div');
        row.className = 'd-flex align-items-center gap-2 py-2 border-bottom';

        const icon = document.createElement('i');
        icon.className = `${TYPE_ICONS[block.type] || 'fas fa-file'} text-muted`;
        row.appendChild(icon);

        const info = document.createElement('div');
        info.className = 'flex-grow-1 text-truncate';
        const title = document.createElement('div');
        title.className = 'text-truncate';
        title.style.fontSize = '0.9rem';
        title.textContent = block.title || 'Quelle';
        info.appendChild(title);
        if (block.locator) {
            const locator = document.createElement('div');
            locator.className = 'text-muted text-truncate';
            locator.style.fontSize = '0.8rem';
            locator.textContent = block.locator;
            info.appendChild(locator);
        }
        row.appendChild(info);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'btn-ghost p-0';
        remove.title = 'Quelle entfernen';
        remove.textContent = '✕';
        remove.addEventListener('click', () => removeBlockAt(index));
        row.appendChild(remove);

        return row;
    }

    function render(blocks) {
        list.innerHTML = '';
        const isEmpty = !blocks || blocks.length === 0;
        if (emptyHint) emptyHint.classList.toggle('d-none', !isEmpty);
        if (isEmpty) return;
        blocks.forEach((block, index) => list.appendChild(buildRow(block, index)));
    }

    onBlocksChanged(render);
    render(getBlocks());

    initExcerptSection();
    initLibrarySection(panel);
}

function initExcerptSection() {
    const textarea = document.getElementById('excerptText');
    const titleInput = document.getElementById('excerptTitle');
    const locatorInput = document.getElementById('excerptLocator');
    const counter = document.getElementById('excerptCounter');
    const pasteBtn = document.getElementById('excerptPasteBtn');
    const addBtn = document.getElementById('excerptAddBtn');
    const errorBox = document.getElementById('excerptError');
    if (!textarea || !addBtn) return;

    function showError(message) {
        errorBox.textContent = message;
        errorBox.classList.remove('d-none');
    }

    function hideError() {
        errorBox.classList.add('d-none');
    }

    function updateCounter() {
        const len = textarea.value.length;
        counter.textContent = `${len.toLocaleString('de-DE')} / 15.000`;
        const over = len > MAX_EXCERPT_CHARS;
        counter.classList.toggle('text-danger', over);
        counter.classList.toggle('text-muted', !over);
        return over;
    }

    function applyPastedText(text) {
        const parsed = parseLogosCitation(text);
        textarea.value = parsed.content;
        if (parsed.title) titleInput.value = parsed.title;
        if (parsed.locator) locatorInput.value = parsed.locator;
        updateCounter();
    }

    textarea.addEventListener('input', updateCounter);

    pasteBtn.addEventListener('click', async () => {
        hideError();
        if (!navigator.clipboard || !navigator.clipboard.readText) {
            showError('Automatisches Einfügen wird hier nicht unterstützt. Bitte fügen Sie den Text manuell ein (Strg+V).');
            textarea.focus();
            return;
        }
        try {
            const text = await navigator.clipboard.readText();
            if (!text) {
                showError('Die Zwischenablage ist leer.');
                return;
            }
            applyPastedText(text);
        } catch (e) {
            showError('Zugriff auf die Zwischenablage nicht möglich. Bitte fügen Sie den Text manuell ein (Strg+V).');
            textarea.focus();
        }
    });

    addBtn.addEventListener('click', () => {
        hideError();
        const content = textarea.value.trim();
        if (!content) {
            showError('Bitte fügen Sie zuerst einen Auszug ein.');
            return;
        }
        if (textarea.value.length > MAX_EXCERPT_CHARS) {
            showError('Der Auszug überschreitet 15.000 Zeichen. Bitte kürzen Sie ihn.');
            return;
        }
        addBlock({
            type: 'logos_excerpt',
            title: titleInput.value.trim() || 'Logos-Auszug',
            locator: locatorInput.value.trim(),
            content
        });
        textarea.value = '';
        titleInput.value = '';
        locatorInput.value = '';
        updateCounter();
    });
}

// --- Section 3: Logos library via the bridge --------------------------------

// Reveal the offline-CTA variant matching the visitor's platform (§5.2) so
// macOS/mobile users are never offered a Windows installer. "other" desktops
// get the generic "use your Windows PC" notice.
function revealOfflineCta() {
    const idByPlatform = {
        windows: 'bridgeOfflineWindows',
        macos: 'bridgeOfflineMac',
        mobile: 'bridgeOfflineMobile',
        other: 'bridgeOfflineMobile'
    };
    const cta = document.getElementById(idByPlatform[getPlatform()]);
    if (cta) cta.classList.remove('d-none');
}

// Passive, dismissible "please update" banner (§5.5, Decision 9). Pure
// client-side semver compare against the version injected by the chat route;
// never blocks usage, never downloads anything by itself.
const UPDATE_BANNER_DISMISSED_KEY = 'shepwiseBridgeUpdateBannerDismissed';

function maybeShowUpdateBanner(bridgeVersion) {
    const expected = window.EXPECTED_BRIDGE_VERSION;
    if (!expected || !bridgeVersion) return;
    if (compareVersions(bridgeVersion, expected) >= 0) return;
    if (sessionStorage.getItem(UPDATE_BANNER_DISMISSED_KEY)) return;
    if (document.getElementById('bridgeUpdateBanner')) return;

    const banner = document.createElement('div');
    banner.id = 'bridgeUpdateBanner';
    banner.className = 'alert alert-info alert-dismissible fade show mb-0';
    banner.setAttribute('role', 'alert');

    const text = document.createElement('span');
    text.textContent = 'Eine neue Version der Logos-Bridge ist verfügbar. ';
    banner.appendChild(text);

    const link = document.createElement('a');
    link.href = '/logos-setup';
    link.target = '_blank';
    link.rel = 'noopener';
    link.className = 'alert-link';
    link.textContent = 'Jetzt aktualisieren';
    banner.appendChild(link);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'btn-close';
    close.setAttribute('data-bs-dismiss', 'alert');
    close.setAttribute('aria-label', 'Schließen');
    banner.appendChild(close);

    banner.addEventListener('closed.bs.alert', () => {
        sessionStorage.setItem(UPDATE_BANNER_DISMISSED_KEY, '1');
    });

    const host = document.querySelector('main') || document.body;
    host.prepend(banner);
}

function initLibrarySection(panel) {
    const probing = document.getElementById('bridgeProbing');
    const offline = document.getElementById('bridgeOffline');
    const online = document.getElementById('bridgeOnline');
    if (!probing || !offline || !online) return;

    revealOfflineCta();

    let panelOpen = false;
    let pollTimer = null;

    // Polls while the panel is open and the bridge is offline (§5.2), so a
    // user who installs the bridge with the panel open sees the library
    // section light up without reopening it. Stops once detected.
    async function poll() {
        const health = await probeHealth();
        probing.classList.add('d-none');
        bridgeDetected = health !== null;
        online.classList.toggle('d-none', !bridgeDetected);
        offline.classList.toggle('d-none', bridgeDetected);
        if (bridgeDetected) {
            maybeShowUpdateBanner(health.version);
            return;
        }
        if (panelOpen) pollTimer = setTimeout(poll, BRIDGE_POLL_INTERVAL_MS);
    }

    panel.addEventListener('show.bs.offcanvas', () => {
        panelOpen = true;
        if (!bridgeDetected) poll();
    });
    panel.addEventListener('hide.bs.offcanvas', () => {
        panelOpen = false;
        clearTimeout(pollTimer);
    });

    initSearchBox('catalogSearchInput', 'catalogSearchBtn', 'catalogResults',
        query => bridgeSearch('/catalog/search', query), buildCatalogRow);
    initSearchBox('notesSearchInput', 'notesSearchBtn', 'notesResults',
        query => bridgeSearch('/notes/search', query), buildNoteRow);
}

function initSearchBox(inputId, btnId, resultsId, search, buildRow) {
    const input = document.getElementById(inputId);
    const button = document.getElementById(btnId);
    const results = document.getElementById(resultsId);
    if (!input || !button || !results) return;

    async function run() {
        const query = input.value.trim();
        if (query.length < 2) return;
        results.textContent = 'Suche …';
        results.className = 'text-muted small mb-3';
        try {
            const entries = await search(query);
            results.innerHTML = '';
            if (entries.length === 0) {
                results.textContent = 'Keine Treffer.';
                return;
            }
            results.className = 'mb-3';
            entries.forEach(entry => results.appendChild(buildRow(entry)));
        } catch (e) {
            results.textContent = 'Suche fehlgeschlagen. Läuft die Bridge noch?';
            results.className = 'text-danger small mb-3';
        }
    }

    button.addEventListener('click', run);
    input.addEventListener('keydown', event => {
        if (event.key === 'Enter') {
            event.preventDefault();
            run();
        }
    });
}

// Result rows contain library data (user-controlled via Logos); build them
// with DOM APIs and textContent so everything stays escaped.
function buildResultRow(iconClass, title, subtitle) {
    const row = document.createElement('div');
    row.className = 'd-flex align-items-center gap-2 py-1 border-bottom';

    const icon = document.createElement('i');
    icon.className = `${iconClass} text-muted`;
    row.appendChild(icon);

    const info = document.createElement('div');
    info.className = 'flex-grow-1 text-truncate';
    const titleEl = document.createElement('div');
    titleEl.className = 'text-truncate';
    titleEl.style.fontSize = '0.85rem';
    titleEl.textContent = title;
    info.appendChild(titleEl);
    if (subtitle) {
        const subtitleEl = document.createElement('div');
        subtitleEl.className = 'text-muted text-truncate';
        subtitleEl.style.fontSize = '0.75rem';
        subtitleEl.textContent = subtitle;
        info.appendChild(subtitleEl);
    }
    row.appendChild(info);
    return row;
}

function buildOpenButton(url) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn-ghost p-0';
    button.title = 'In Logos öffnen';
    button.innerHTML = '<i class="fas fa-arrow-up-right-from-square"></i>';
    button.addEventListener('click', () => {
        openInLogos(url).catch(() => {
            button.title = 'Öffnen fehlgeschlagen — läuft die Bridge noch?';
        });
    });
    return button;
}

function buildCatalogRow(entry) {
    const row = buildResultRow('fas fa-book', entry.title, entry.author);
    row.appendChild(buildOpenButton(`logosres:${entry.resource_id}`));
    return row;
}

function buildNoteRow(entry) {
    if (entry.kind === 'highlight') {
        // Highlights carry no text (FR-21 spike) — reference-only, openable.
        const row = buildResultRow('fas fa-highlighter', entry.title, 'Ohne Text');
        row.appendChild(buildOpenButton(`logosres:${entry.anchor_reference}`));
        return row;
    }
    const row = buildResultRow('fas fa-note-sticky', entry.title,
        entry.content_text);
    const attach = document.createElement('button');
    attach.type = 'button';
    attach.className = 'btn-ghost p-0';
    attach.title = 'Als Quelle anhängen';
    attach.innerHTML = '<i class="fas fa-plus"></i>';
    attach.addEventListener('click', () => {
        addBlock({
            type: 'logos_note',
            title: entry.title,
            locator: entry.anchor_reference || '',
            content: entry.content_text
        });
    });
    row.appendChild(attach);
    return row;
}
